import React, { memo, useRef, useMemo, useCallback, useState } from 'react';
import type { Comment, CommentListProps } from './CommentList.types';
import { LoadMoreList } from '../../components/LoadMoreList';
import {
  DEFAULT_INITIAL_VISIBLE,
  DEFAULT_LOAD_MORE_COUNT
} from '../../components/LoadMoreList/LoadMoreList';
import { CommentSection } from './CommentSection';
import type { CommentCardProps } from '../../components/CommentCard';
import { useLoadWhenVisible } from '../../hooks/useLoadWhenVisible';

type LoadMoreVirtualizationConfig = Extract<
  NonNullable<CommentListProps['useVirtualization']>,
  { type: 'LOAD_MORE' }
>;

export interface CommentListLoadMoreProps {
  unresolvedComments: Comment[];
  resolvedComments: Comment[];
  getCardPropsFor: (comment: Comment) => CommentCardProps;
  /** LOAD_MORE virtualization config (or undefined for defaults). */
  useVirtualization?: LoadMoreVirtualizationConfig | undefined;
  containerProps: React.ComponentProps<'div'>;
}

const CommentListLoadMoreComponent: React.FC<CommentListLoadMoreProps> = ({
  unresolvedComments,
  resolvedComments,
  getCardPropsFor,
  useVirtualization,
  containerProps
}) => {
  const {
    initialVisibleCount = DEFAULT_INITIAL_VISIBLE,
    loadMoreCount = DEFAULT_LOAD_MORE_COUNT,
    lookbackCount,
    initialStartIndex,
    autoLoadMore = true,
    autoLoadPrev = true
  } = useVirtualization ?? {};
  const combined = useMemo(
    () => [...unresolvedComments, ...resolvedComments],
    [unresolvedComments, resolvedComments]
  );

  const onLoadMoreRef = useRef<() => void>(() => {});
  const onLoadPrevRef = useRef<() => void>(() => {});
  const hasMoreRef = useRef(false);
  const hasPrevRef = useRef(false);
  const [loadMoreButtonEl, setLoadMoreButtonEl] =
    useState<HTMLButtonElement | null>(null);
  const [loadPrevButtonEl, setLoadPrevButtonEl] =
    useState<HTMLButtonElement | null>(null);
  /** ID of the item currently at the top of the visible cards (before loading prev). */
  const topVisibleItemIdRef = useRef<string | null>(null);

  const loadPrevButtonRefCallback = useCallback(
    (el: HTMLButtonElement | null) => {
      setLoadPrevButtonEl(prev => (prev === el ? prev : el));
    },
    []
  );
  const loadMoreButtonRefCallback = useCallback(
    (el: HTMLButtonElement | null) => {
      setLoadMoreButtonEl(prev => (prev === el ? prev : el));
    },
    []
  );

  useLoadWhenVisible(loadMoreButtonEl, {
    enabled: autoLoadMore,
    canLoadRef: hasMoreRef,
    onLoadRef: onLoadMoreRef,
    rootMargin: '100px'
  });
  useLoadWhenVisible(loadPrevButtonEl, {
    enabled: autoLoadPrev,
    canLoadRef: hasPrevRef,
    onLoadRef: onLoadPrevRef,
    rootMargin: '100px'
  });

  const handleLoadMoreClick = useCallback(() => {
    onLoadMoreRef.current();
  }, []);
  const handleLoadPrevClick = useCallback(() => {
    const topId = topVisibleItemIdRef.current;
    if (topId != null) {
      setTimeout(() => {
        document.querySelector(`[data-comment-id="${topId}"]`)?.scrollIntoView({
          block: 'end',
          inline: 'nearest',
          behavior: 'instant' as ScrollBehavior
        });
      }, 100);
    }
    onLoadPrevRef.current();
  }, []);

  return (
    <div {...containerProps}>
      <LoadMoreList<Comment>
        items={combined}
        initialVisibleCount={initialVisibleCount}
        loadMoreCount={loadMoreCount}
        lookbackCount={lookbackCount}
        initialStartIndex={initialStartIndex}
      >
        {(visibleItems, { onLoadMore, hasMore, onLoadPrev, hasPrev }) => {
          onLoadMoreRef.current = onLoadMore;
          onLoadPrevRef.current = onLoadPrev;
          hasMoreRef.current = hasMore;
          hasPrevRef.current = hasPrev;
          topVisibleItemIdRef.current =
            visibleItems.length > 0 ? visibleItems[0].id : null;
          const visibleUnresolved = visibleItems.filter(
            c => c.commentStatus !== 'RESOLVED'
          );
          const visibleResolved = visibleItems.filter(
            c => c.commentStatus === 'RESOLVED'
          );

          return (
            <>
              {hasPrev && (
                <button
                  ref={loadPrevButtonRefCallback}
                  type="button"
                  className="comment-list__load-prev"
                  onClick={handleLoadPrevClick}
                >
                  Load previous
                </button>
              )}
              <CommentSection
                comments={visibleUnresolved}
                getCardPropsFor={getCardPropsFor}
                useVirtualizer={false}
              />
              {visibleUnresolved.length > 0 && visibleResolved.length > 0 && (
                <div className="comment-list__divider" />
              )}
              <div className="comment-list__resolved-section">
                <CommentSection
                  comments={visibleResolved}
                  getCardPropsFor={getCardPropsFor}
                  useVirtualizer={false}
                />
              </div>
              {hasMore && (
                <button
                  ref={loadMoreButtonRefCallback}
                  type="button"
                  className="comment-list__load-more"
                  onClick={handleLoadMoreClick}
                >
                  Load more
                </button>
              )}
            </>
          );
        }}
      </LoadMoreList>
    </div>
  );
};

export const CommentListLoadMore = memo(CommentListLoadMoreComponent);
